﻿import { httpArg, httpPost, httpRoute } from '../infrastructure';
import { LocalizedString } from '../dto/localized-string';

export interface RegisterUserDto {
	email: string;
	password: string;
	firstName: string;
	lastName?: string;
	invitedByUserId?: string;
}

export interface EmailVerificationResult {
	success: boolean;
	error?: LocalizedString;
	errorCode?: 'missing_token' | 'invalid_token' | 'expired_token' | 'unknown';
}

/**
 * Response for login operation
 */
export interface LoginResponse {
	success: boolean;
	message: LocalizedString;
}

/**
 * Response for register operation
 */
export interface RegisterResponse {
	success: boolean;
	message: LocalizedString;
}

/**
 * Response for forgot password operation
 */
export interface ForgotPasswordResponse {
	success: boolean;
	message: LocalizedString;
}

/**
 * Response for reset password operation
 */
export interface ResetPasswordResponse {
	success: boolean;
	message: LocalizedString;
}

/**
 * Response for email verification operation
 */
export interface VerifyEmailResponse {
	success: boolean;
	message: LocalizedString;
}

/**
 * Response for resend verification operation
 */
export interface ResendVerificationResponse {
	success: boolean;
	message: LocalizedString;
}

/**
 * Response for password validation operation
 */
export interface PasswordValidationResponse {
	isValid: boolean;
	errors: string[];
	strength: number;
	feedback: string[];
}

/**
 * Email authentication service interface.
 * Provides all authentication-related operations with HTTP decorators for auto-generated endpoints.
 */
@httpRoute('/api/email-auth')
export abstract class IEmailAuthService {
	/**
	 * Login with email and password
	 */
	@httpPost('login')
	login(
		@httpArg('email') email: string,
		@httpArg('password') password: string
	): Promise<LoginResponse> {
		void email;
		void password;
		throw new Error('Not implemented');
	}

	/**
	 * Register a new user
	 */
	@httpPost('register')
	register(
		@httpArg('email') email: string,
		@httpArg('password') password: string,
		@httpArg('invitedByUserId') invitedByUserId?: string
	): Promise<RegisterResponse> {
		void email;
		void password;
		void invitedByUserId;
		throw new Error('Not implemented');
	}

	/**
	 * Request password reset (forgot password)
	 */
	@httpPost('forgot-password')
	forgotPassword(
		@httpArg('email') email: string
	): Promise<ForgotPasswordResponse> {
		void email;
		throw new Error('Not implemented');
	}

	/**
	 * Reset password with token
	 */
	@httpPost('reset-password')
	resetPassword(
		@httpArg('token') token: string,
		@httpArg('newPassword') newPassword: string
	): Promise<ResetPasswordResponse> {
		void token;
		void newPassword;
		throw new Error('Not implemented');
	}

	/**
	 * Verify email with token
	 */
	@httpPost('verify-email')
	verifyEmail(
		@httpArg('token') token: string | undefined
	): Promise<EmailVerificationResult> {
		void token;
		throw new Error('Not implemented');
	}

	/**
	 * Resend verification email
	 */
	@httpPost('resend-verification')
	resendVerification(
		@httpArg('email') email: string
	): Promise<ResendVerificationResponse> {
		void email;
		throw new Error('Not implemented');
	}

	/**
	 * Validate password strength
	 */
	@httpPost('validate-password')
	validatePassword(
		@httpArg('password') password: string
	): Promise<PasswordValidationResponse> {
		void password;
		throw new Error('Not implemented');
	}

	/**
	 * Change password for authenticated user
	 */
	@httpPost('change-password')
	changePassword(
		@httpArg('userId') userId: string,
		@httpArg('oldPassword') oldPassword: string,
		@httpArg('newPassword') newPassword: string
	): Promise<void> {
		void userId;
		void oldPassword;
		void newPassword;
		throw new Error('Not implemented');
	}

	/**
	 * Get the Google OAuth login URL for redirect
	 */
	@httpPost('google-login-url')
	getGoogleLoginUrl(): Promise<{ url: string }> {
		throw new Error('Not implemented');
	}
}
